import React, { useEffect, useState } from "react";
import { Row, Col, Progress, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import antivirusService from "../../services/antivirusService";

const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const StatCard = ({ icon, label, value, color, sub }) => (
  <div style={{
    padding: "14px 16px", borderRadius: 8, height: "100%",
    background: "rgba(var(--vz-primary-rgb),.04)",
    border: "1px solid var(--vz-border-color)"
  }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <span style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px",
        color: "var(--vz-secondary-color)", fontWeight: 700
      }}>
        {label}
      </span>
      <div style={{
        width: 28, height: 28, borderRadius: 8, display: "flex",
        alignItems: "center", justifyContent: "center",
        background: `${color}18`, color
      }}>
        <FeatherIcon icon={icon} size={13} />
      </div>
    </div>
    <p style={{ fontSize: 22, fontWeight: 800, color, margin: "0 0 2px" }}>{value ?? "—"}</p>
    {sub && <p style={{ color: "var(--vz-secondary-color)", fontSize: 11, margin: 0 }}>{sub}</p>}
  </div>
);

const ActiveScansOverview = () => {
  const [stats, setStats]   = useState(null);
  const [scans, setScans]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);

  const load = async () => {
    try {
      const [statsRes, scansRes] = await Promise.all([
        antivirusService.getStatistics(),
        antivirusService.getAgentScans()
      ]);
      if (statsRes.success) setStats(statsRes.statistics);
      if (scansRes.success) setScans(scansRes.scans || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const total       = stats?.total_scans || 0;
  const infected    = stats?.infected_scans || 0;
  const threatRate  = total > 0 ? (infected / total) * 100 : 0;
  const activeScans = stats?.active_scans || 0;

  const totalFiles = scans.reduce((acc, s) => acc + (s.files_scanned || 0), 0);
  const totalDataMB = scans.reduce((acc, s) => {
    const num = parseFloat(s.data_scanned);
    return acc + (isNaN(num) ? 0 : num);
  }, 0);
  const totalData = totalDataMB >= 1000
    ? `${(totalDataMB / 1000).toFixed(1)} GB`
    : `${totalDataMB.toFixed(0)} MB`;

  const lastScanDate  = stats?.last_scan ? new Date(stats.last_scan) : null;
  const lastScanLine1 = lastScanDate
    ? lastScanDate.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : "N/A";
  const lastScanLine2 = lastScanDate
    ? lastScanDate.toLocaleTimeString([], { timeStyle: "short" })
    : "";

  return (
    <div style={{ ...card, height: "100%" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Scan Activity</span>
          <p style={{ color: "var(--vz-secondary-color)", fontSize: 12, margin: 0 }}>Live scan metrics across all endpoints</p>
        </div>
        {loading ? (
          <Spinner size="sm" color="primary" />
        ) : activeScans > 0 ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: "rgba(var(--vz-primary-rgb),.1)",
            border: "1px solid rgba(var(--vz-primary-rgb),.25)",
            color: "var(--vz-primary)"
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--vz-primary)", animation: "avPulse 2s infinite" }} />
            {activeScans} Scanning
          </span>
        ) : (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: "rgba(var(--vz-success-rgb),.1)",
            border: "1px solid rgba(var(--vz-success-rgb),.25)",
            color: "var(--vz-success)"
          }}>
            <FeatherIcon icon="check-circle" size={12} />All Idle
          </span>
        )}
      </div>

      <div style={{ padding: "18px 18px 14px" }}>
        <Row className="g-3 mb-4">
          <Col md={3}>
            <StatCard icon="search"     label="Total Scans"    value={total}                     color="var(--vz-primary)"  sub="across all endpoints" />
          </Col>
          <Col md={3}>
            <StatCard icon="file-text"  label="Files Scanned"  value={totalFiles.toLocaleString()} color="var(--vz-info)"    sub="combined total" />
          </Col>
          <Col md={3}>
            <StatCard icon="hard-drive" label="Data Scanned"   value={totalData}                 color="#6f42c1"            sub="total processed" />
          </Col>
          <Col md={3}>
            <StatCard icon="clock"      label="Last Scan"      value={lastScanLine1}             color="var(--vz-warning)"  sub={lastScanLine2} />
          </Col>
        </Row>

        {/* Threat Rate Bar */}
        <div style={{
          padding: "14px 16px", borderRadius: 8,
          background: "rgba(var(--vz-primary-rgb),.04)",
          border: "1px solid var(--vz-border-color)"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="bar-chart-2" size={13} />Threat Detection Rate
            </span>
            <span style={{ fontWeight: 700, fontSize: 13, color: threatRate > 5 ? "var(--vz-danger)" : "var(--vz-success)" }}>
              {threatRate.toFixed(1)}%
            </span>
          </div>
          <Progress
            value={Math.max(threatRate, 0.5)}
            style={{ height: 6, borderRadius: 10, background: "var(--vz-border-color)" }}
            barStyle={{
              background: threatRate > 5
                ? "linear-gradient(90deg, var(--vz-danger), #e03e20)"
                : "linear-gradient(90deg, var(--vz-success), #1a9e6e)",
              borderRadius: 10
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", display: "flex", alignItems: "center", gap: 4 }}>
              <FeatherIcon icon="shield" size={11} style={{ color: "var(--vz-success)" }} />
              {stats?.clean_scans || 0} clean
            </span>
            <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", display: "flex", alignItems: "center", gap: 4 }}>
              <FeatherIcon icon="alert-circle" size={11} style={{ color: "var(--vz-danger)" }} />
              {infected} infected
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes avPulse {
          0%,100% { box-shadow: 0 0 0 2px rgba(var(--vz-primary-rgb),.3); }
          50%      { box-shadow: 0 0 0 5px rgba(var(--vz-primary-rgb),.08); }
        }
      `}</style>
    </div>
  );
};

export default ActiveScansOverview;
